import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";

interface InfiniteScrollProps<T> {
  items: T[];
  loadMore: () => Promise<void>;
  hasMore: boolean;
  isLoading: boolean;
  renderItem: (item: T) => React.ReactNode;
}

const InfiniteScroll = <T,>({
  items,
  loadMore,
  hasMore,
  isLoading,
  renderItem,
}: InfiniteScrollProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (
        containerRef.current &&
        containerRef.current.scrollHeight -
          containerRef.current.scrollTop <=
          containerRef.current.clientHeight + 50
      ) {
        if (hasMore && !isLoading) {
          loadMore();
        }
      }
    };

    const container = containerRef.current;
    container?.addEventListener("scroll", handleScroll);

    return () => container?.removeEventListener("scroll", handleScroll);
  }, [hasMore, isLoading, loadMore]);

  return (
    <Box
      ref={containerRef}
      sx={{
        height: "100%",
        overflowY: "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>{renderItem(item)}</React.Fragment>
      ))}
      {isLoading && <CircularProgress sx={{ marginTop: "20px" }} />}
      {!hasMore && !isLoading && (
        <Box sx={{ marginTop: "20px", color: "gray" }}>No hay más datos</Box>
      )}
    </Box>
  );
};

export default InfiniteScroll;
